import { authenticate } from '@cian/auth/lib/authenticator';
import { TSubscribeStatus } from '@cian/reviews-subscribe-component';

import { subscribe, unsubscribe } from '../api/subscribe_reviews';
import {
  ISubscribeReviews,
  ISubscribeReviewsClosePopup,
  ISubscribeReviewsEmailChange,
  ISubscribeReviewsEmailError,
  ISubscribeReviewsError,
  ISubscribeReviewsFetch,
  ISubscribeReviewsOpenPopup,
  ISubscribeReviewsSetStatus,
  ISubscribeReviewsSuccess,
  IUnsubscribeReviewsError,
  IUnsubscribeReviewsFetch,
  IUnsubscribeReviewsSuccess,
  TSubscribeReviewsActions,
} from '../models/subscribe_reviews';
import { IAppState, IContext, IThunkActionCreator } from './index';
import { trackSubscribeClick } from './tracking/subscribe_reviews';
import { TThunkDispatch } from '../types/redux';

export interface ISubscribeSuccessResponseSchema {
  status: 'ok';
  data: ISubscribeSuccessResponseDataSchema;
}

export interface ISubscribeSuccessResponseDataSchema {
  login: string;
  logOnInfo: ILogOnInfo[];
}

export interface ILogOnInfo {
  logOnUrl: string;
  token: string;
}

export function subscribeOpenPopup(): ISubscribeReviewsOpenPopup {
  return {
    type: 'ISubscribeReviewsOpenPopup',
  };
}

export function subscribeClosePopup(): ISubscribeReviewsClosePopup {
  return {
    type: 'ISubscribeReviewsClosePopup',
  };
}

export function subscribeChangeEmail(email: string | undefined): ISubscribeReviewsEmailChange {
  return {
    payload: { email },
    type: 'ISubscribeReviewsEmailChange',
  };
}

export function setEmailError(errorText: string): ISubscribeReviewsEmailError {
  return {
    payload: { errorText },
    type: 'ISubscribeReviewsEmailError',
  };
}

export function setSubscribeStatus(subscribeStatus: TSubscribeStatus): ISubscribeReviewsSetStatus {
  return {
    payload: {
      subscribeStatus,
    },
    type: 'ISubscribeReviewsSetStatus',
  };
}

/**
 * Таймаут для отображения попапа успешной подписки/отписки на отзывы
 */
const TIMEOUT_VISIBLE = 2000;

export function onUnsubscribe(email: string): IThunkActionCreator {
  return (dispatch: TThunkDispatch, getState: () => IAppState, context: IContext) => {
    dispatch<IUnsubscribeReviewsFetch>({ type: 'IUnsubscribeReviewsFetch' });

    const { entityId } = getState().pageData;

    return unsubscribe(context.httpApi, email, entityId)
      .then(() => {
        dispatch<IUnsubscribeReviewsSuccess>({
          payload: {
            subscribeStatus: 'unsubscribeSuccess',
          },
          type: 'IUnsubscribeReviewsSuccess',
        });

        setTimeout(() => {
          dispatch<ISubscribeReviewsClosePopup>({ type: 'ISubscribeReviewsClosePopup' });

          dispatch<ISubscribeReviewsSetStatus>({
            payload: {
              subscribeStatus: 'noSubscribe',
            },
            type: 'ISubscribeReviewsSetStatus',
          });
          trackSubscribeClick(false);
        }, TIMEOUT_VISIBLE);
      })
      .catch(() => {
        dispatch<IUnsubscribeReviewsError>({
          payload: {
            subscribeStatus: 'unsubscribeError',
          },
          type: 'IUnsubscribeReviewsError',
        });
      });
  };
}

export function onSubscribe(email: string): IThunkActionCreator {
  return (dispatch: TThunkDispatch, getState: () => IAppState, context: IContext) => {
    dispatch<ISubscribeReviewsFetch>({ type: 'ISubscribeReviewsFetch' });

    const { entityId } = getState().pageData;

    return subscribe(context.httpApi, email, entityId)
      .then((response: ISubscribeSuccessResponseSchema) => {
        /**
         * Если в response.data пришло не null, значит текущий пользователь не авторизован
         * и ввел почту, которой еще не существует в базе ЦИАН, мы его авторегистрируем и авторизуем.
         */
        if (response.data !== null) {
          authenticate(response.data.logOnInfo, response.data.login, true).then(() => window.location.reload());

          return;
        }

        dispatch<ISubscribeReviewsSuccess>({
          payload: {
            subscribeStatus: 'subscribeSuccess',
          },
          type: 'ISubscribeReviewsSuccess',
        });

        setTimeout(() => {
          const { user } = getState();

          dispatch<ISubscribeReviewsClosePopup>({ type: 'ISubscribeReviewsClosePopup' });

          if (user.isAuthenticated) {
            dispatch<ISubscribeReviewsSetStatus>({
              payload: {
                subscribeStatus: 'subscribe',
              },
              type: 'ISubscribeReviewsSetStatus',
            });
            trackSubscribeClick(true);
          } else {
            /**
             * Не авторизованный пользователь не может отписываться от подписки на отзывы
             * на самой странице отзывов, только через почту.
             */
            dispatch<ISubscribeReviewsSetStatus>({
              payload: {
                subscribeStatus: 'noSubscribe',
              },
              type: 'ISubscribeReviewsSetStatus',
            });
          }
        }, TIMEOUT_VISIBLE);
      })
      .catch(() => {
        dispatch<ISubscribeReviewsError>({
          payload: {
            subscribeStatus: 'subscribeError',
          },
          type: 'ISubscribeReviewsError',
        });
      });
  };
}

const initialState: ISubscribeReviews = {
  emailError: '',
  isFetching: false,
  isOpenSubscribeReviewsPopup: false,
  subscribeEmail: undefined,
  subscribeStatus: undefined,
};

export function subscribeReviewsReducer(state: ISubscribeReviews = initialState, action: TSubscribeReviewsActions) {
  switch (action.type) {
    case 'ISubscribeReviewsOpenPopup':
      return {
        ...state,
        isOpenSubscribeReviewsPopup: true,
      };

    case 'ISubscribeReviewsClosePopup': {
      let newSubscribeStatus: TSubscribeStatus | undefined;

      const { subscribeStatus } = state;

      /**
       * При ошибки подписки и закрытии попапа подписки - сбрасываем состояние до "не подписан"
       */
      if (subscribeStatus === 'subscribeError') {
        newSubscribeStatus = 'noSubscribe';
      }

      /**
       * Если пытались отписаться и произошла ошибка и закрыти попап отписки - возвращаем "подписан"
       */
      if (subscribeStatus === 'unsubscribeError') {
        newSubscribeStatus = 'subscribe';
      }

      return {
        ...state,
        emailError: '',
        isOpenSubscribeReviewsPopup: false,
        /**
         * Если пользователь вводил что-либо в input для почты и не стал подписываться на отзывы, а закрыл
         * попап - сбрасываем введеные им данные либо на пустую строку, либо на его почту, если он авторизован
         */
        subscribeEmail: subscribeStatus === 'noSubscribe' ? undefined : state.subscribeEmail,
        subscribeStatus: newSubscribeStatus ? newSubscribeStatus : state.subscribeStatus,
      };
    }
    case 'ISubscribeReviewsEmailChange':
      return {
        ...state,
        emailError: '',
        subscribeEmail: action.payload.email,
      };

    case 'ISubscribeReviewsEmailError':
      return {
        ...state,
        emailError: action.payload.errorText,
      };

    case 'ISubscribeReviewsFetch':
    case 'IUnsubscribeReviewsFetch':
      return {
        ...state,
        isFetching: true,
      };

    case 'ISubscribeReviewsError':
    case 'IUnsubscribeReviewsError':
      return {
        ...state,
        isFetching: false,
        subscribeStatus: action.payload.subscribeStatus,
      };

    case 'ISubscribeReviewsSuccess':
    case 'IUnsubscribeReviewsSuccess':
      return {
        ...state,
        isFetching: false,
        subscribeStatus: action.payload.subscribeStatus,
      };

    case 'ISubscribeReviewsSetStatus':
      return {
        ...state,
        subscribeStatus: action.payload.subscribeStatus,
      };

    default:
      return state;
  }
}
